import React, { useState } from "react";
import { PlusCircle, AlertCircle, FileUp, CheckCircle2 } from "lucide-react";
import { motion } from "motion/react";
import DOMPurify from "dompurify";
import { Mantra } from "../types";
import { validateCustomMantraInput } from "@sovereign/validators";

interface CustomMantraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMantra: (mantra: Mantra) => void;
}

export function CustomMantraModal({ isOpen, onClose, onAddMantra }: CustomMantraModalProps) {
  const [newMantra, setNewMantra] = useState({
    title: "",
    sanskrit: "",
    iast: "",
    translation: "",
    significance: "",
    segmentsRaw: ""
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [importStatus, setImportStatus] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleJsonImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonContent = JSON.parse(event.target?.result as string);
        const parseResult = validateCustomMantraInput(jsonContent);

        if (!parseResult.success) {
          const errors = parseResult.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`);
          setValidationErrors(errors);
          setImportStatus("Validation failed for imported JSON.");
          return;
        }

        const validData = parseResult.data;
        const mantraToAdd: Mantra = {
          id: "mantra_" + Date.now(),
          tradition: validData.tradition as any,
          tongue: validData.tongue as any,
          title: DOMPurify.sanitize(validData.title),
          sanskrit: DOMPurify.sanitize(validData.sanskrit),
          iast: DOMPurify.sanitize(validData.iast),
          translation: DOMPurify.sanitize(validData.translation),
          significance: DOMPurify.sanitize(validData.significance),
          phoneticSegments: validData.phoneticSegments as any,
          pitchZones: validData.pitchZones.length > 0 ? (validData.pitchZones as any) : [
            { label: "Udatta (High)", minHz: 180, maxHz: 240, description: "High Pitch" },
            { label: "Svarita (Middle)", minHz: 140, maxHz: 179, description: "Circumflex" },
            { label: "Anudatta (Low)", minHz: 100, maxHz: 139, description: "Low Pitch" }
          ],
          pronunciationTips: validData.pronunciationTips.length > 0 ? validData.pronunciationTips : ["Aim for clear vowel articulation."]
        };

        onAddMantra(mantraToAdd);
        setImportStatus("Mantra JSON imported and validated successfully!");
        setTimeout(() => {
          onClose();
        }, 1000);
      } catch (err) {
        setValidationErrors(["Invalid JSON file format."]);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);
    setImportStatus(null);

    const cleanTitle = DOMPurify.sanitize(newMantra.title.trim());
    const cleanSanskrit = DOMPurify.sanitize(newMantra.sanskrit.trim());
    const cleanIast = DOMPurify.sanitize(newMantra.iast.trim());
    const cleanTranslation = DOMPurify.sanitize((newMantra.translation || "User defined custom translation").trim());
    const cleanSignificance = DOMPurify.sanitize((newMantra.significance || "Vedic practice").trim());

    let segments = [];
    if (newMantra.segmentsRaw.trim()) {
      segments = newMantra.segmentsRaw.split(",").map(item => {
        const [syllable, pitch, type] = item.split(":");
        let normPitch = "Svarita";
        if (pitch?.toLowerCase().includes("udatta") || pitch?.toLowerCase().includes("high")) normPitch = "Udatta";
        if (pitch?.toLowerCase().includes("anudatta") || pitch?.toLowerCase().includes("low")) normPitch = "Anudatta";

        return {
          syllable: DOMPurify.sanitize(syllable?.trim() || "Om"),
          expectedPitch: normPitch,
          phoneticType: DOMPurify.sanitize(type?.trim() || "General"),
          description: "Custom user segment"
        };
      });
    } else {
      segments = cleanIast.split(" ").filter(Boolean).map(word => ({
        syllable: DOMPurify.sanitize(word),
        expectedPitch: "Svarita",
        phoneticType: "General Voice",
        description: "Chant steadily"
      }));
    }

    const payload = {
      title: cleanTitle,
      sanskrit: cleanSanskrit,
      iast: cleanIast,
      translation: cleanTranslation,
      significance: cleanSignificance,
      tongue: "sanskrit",
      tradition: "Vedic Practitioners",
      phoneticSegments: segments
    };

    const parseResult = validateCustomMantraInput(payload);
    if (!parseResult.success) {
      const errors = parseResult.error.issues.map((issue) => `${issue.message}`);
      setValidationErrors(errors);
      return;
    }

    const validData = parseResult.data;

    const mantraToAdd: Mantra = {
      id: "mantra_" + Date.now(),
      tradition: validData.tradition as any,
      tongue: validData.tongue as any,
      title: validData.title,
      sanskrit: validData.sanskrit,
      iast: validData.iast,
      translation: validData.translation,
      significance: validData.significance,
      phoneticSegments: validData.phoneticSegments as any,
      pitchZones: [
        { label: "Udatta (High)", minHz: 180, maxHz: 240, description: "High Pitch" },
        { label: "Svarita (Middle)", minHz: 140, maxHz: 179, description: "Circumflex" },
        { label: "Anudatta (Low)", minHz: 100, maxHz: 139, description: "Low Pitch" }
      ],
      pronunciationTips: ["Aim for clear vowel articulation and steady pitch."]
    };

    onAddMantra(mantraToAdd);
    setNewMantra({ title: "", sanskrit: "", iast: "", translation: "", significance: "", segmentsRaw: "" });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 max-w-lg w-full shadow-2xl relative"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-neutral-100 flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-amber-500" /> Save Custom Vedic Mantra
          </h3>
          <label className="text-[11px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-lg cursor-pointer hover:bg-amber-500/20 transition flex items-center gap-1.5">
            <FileUp className="w-3.5 h-3.5" />
            <span>Import JSON</span>
            <input type="file" accept=".json" onChange={handleJsonImport} className="hidden" />
          </label>
        </div>

        <p className="text-xs text-neutral-400 mb-4 leading-normal">
          Strictly validated by <strong className="text-amber-300 font-mono">@sovereign/validators</strong>. Enter Devanagari text, IAST transliteration, and define standard syllables for pitch auto-correlation.
        </p>

        {validationErrors.length > 0 && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs text-red-300 space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-red-400 font-mono uppercase text-[10px]">
              <AlertCircle className="w-4 h-4" /> Validation Error (Zod Schema)
            </div>
            <ul className="list-disc list-inside space-y-0.5 text-[11px] text-red-200">
              {validationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {importStatus && (
          <div className="mb-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-xs text-emerald-300 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>{importStatus}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label className="text-xs font-semibold text-neutral-400 block mb-1">Mantra Title</label>
            <input
              type="text"
              required
              value={newMantra.title}
              onChange={(e) => setNewMantra({ ...newMantra, title: e.target.value })}
              placeholder="e.g. Mahamrityunjaya Mantra"
              className="w-full bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-amber-500"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-neutral-400 block mb-1">Sanskrit (Devanagari Unicode Required)</label>
            <input
              type="text"
              required
              value={newMantra.sanskrit}
              onChange={(e) => setNewMantra({ ...newMantra, sanskrit: e.target.value })}
              placeholder="ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम्..."
              className="w-full bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-amber-500"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-neutral-400 block mb-1">IAST Transliteration</label>
            <input
              type="text"
              required
              value={newMantra.iast}
              onChange={(e) => setNewMantra({ ...newMantra, iast: e.target.value })}
              placeholder="oṃ tryambakaṃ yajāmahe sugandhiṃ..."
              className="w-full bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-400 block mb-1">Translation</label>
              <textarea
                value={newMantra.translation}
                onChange={(e) => setNewMantra({ ...newMantra, translation: e.target.value })}
                placeholder="Enter English meaning"
                className="w-full h-16 bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg p-2.5 outline-none focus:border-amber-500 resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-400 block mb-1">Significance / Tips</label>
              <textarea
                value={newMantra.significance}
                onChange={(e) => setNewMantra({ ...newMantra, significance: e.target.value })}
                placeholder="Enter spiritual importance or guidelines"
                className="w-full h-16 bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg p-2.5 outline-none focus:border-amber-500 resize-none"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-neutral-400">Custom Pitch Segments (Optional)</label>
              <span className="text-[9px] text-neutral-500">Format: Syllable:Pitch:Type, ...</span>
            </div>
            <input
              type="text"
              value={newMantra.segmentsRaw}
              onChange={(e) => setNewMantra({ ...newMantra, segmentsRaw: e.target.value })}
              placeholder="Om:Svarita:Pranava, Tryam:Anudatta:Velar, ba:Udatta:Dental"
              className="w-full bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-amber-500"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-neutral-800">
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-neutral-400 hover:text-neutral-200 bg-neutral-800 px-4 py-2 rounded-lg cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-xs bg-amber-500 text-neutral-950 font-bold px-4 py-2 rounded-lg hover:bg-amber-400 transition cursor-pointer"
            >
              Validate & Save Mantra
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
